import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Wallet as WalletIcon, Loader2, Plus, TrendingUp, Users, Receipt, Gift,
  Settings2, BarChart3, CheckCircle2,
} from "lucide-react";

const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function WalletPage() {
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [custom, setCustom] = useState("");
  const [paying, setPaying] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = useCallback(() => {
    api.get("/wallet").then((r) => setW(r.data)).catch(() => {});
    api.get("/wallet/ledger").then((r) => setLedger(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Gestione ritorno da Stripe: polling stato pagamento (il saldo si aggiorna solo lato backend)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    setChecking(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/wallet/topup/status/${sid}`);
        if (data.payment_status === "paid" && data.credited) {
          toast.success("Ricarica completata · saldo aggiornato");
          setChecking(false);
          window.history.replaceState({}, "", "/wallet");
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Sessione di pagamento scaduta");
          setChecking(false);
          window.history.replaceState({}, "", "/wallet");
          return;
        }
      } catch { /* retry */ }
      if (attempts >= 6) {
        setChecking(false);
        toast.message("Pagamento in elaborazione. Il saldo verrà aggiornato a conferma.");
        window.history.replaceState({}, "", "/wallet");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [load]);

  const bonusFor = (amount) => {
    const tiers = (w?.topup?.bonus || []).slice().sort((a, b) => a.soglia - b.soglia);
    let best = 0;
    tiers.forEach((t) => { if (amount >= t.soglia) best = t.bonus; });
    return best;
  };

  const topup = async (amount) => {
    if (!amount || amount < (w?.topup?.min_custom || 25)) {
      toast.error(`Importo minimo ${eur(w?.topup?.min_custom || 25)}`);
      return;
    }
    setPaying(true);
    try {
      const { data } = await api.post("/wallet/topup/checkout", { amount, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setPaying(false);
    }
  };

  if (!w) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#EA7B2B]" /></div>;

  const b = w.saldo;
  return (
    <div className="space-y-6" data-testid="wallet-page">
      <div>
        <h1 className="text-3xl font-light text-white flex items-center gap-3"><WalletIcon className="w-7 h-7 text-[#EA7B2B]" /> Wallet</h1>
        <p className="text-sm text-zinc-500 mt-1">{w.organizzazione?.nome} · credito prepagato condiviso {b.enforced && "· blocco saldo attivo"}</p>
      </div>

      {checking && (
        <div className="flex items-center gap-2 text-sm text-[#EA7B2B] bg-[#EA7B2B]/10 border border-[#EA7B2B]/30 rounded-sm px-4 py-3" data-testid="wallet-checking">
          <Loader2 className="w-4 h-4 animate-spin" /> Verifica pagamento in corso…
        </div>
      )}

      {/* Saldo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wallet-balance">
        <BalCard label="Saldo disponibile" value={eur(b.disponibile)} accent testid="wallet-disponibile" />
        <BalCard label="Credito acquistato" value={eur(b.acquistato)} />
        <BalCard label="Bonus" value={eur(b.bonus)} icon={Gift} />
        <BalCard label="In prenotazione" value={eur(b.in_prenotazione)} />
      </div>

      {/* Ricarica */}
      <Card title="Ricarica credito" icon={Plus} testid="wallet-topup">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
          {w.topup.tagli.map((t) => (
            <button key={t} onClick={() => topup(t)} disabled={paying} data-testid={`topup-${t}`}
              className="relative bg-[#0A1826] border border-white/10 rounded-sm py-3 hover:border-[#EA7B2B]/60 transition-colors disabled:opacity-50">
              <div className="text-sm font-medium text-white">{eur(t)}</div>
              {bonusFor(t) > 0 && <div className="text-[10px] text-[#10B981]">+{eur(bonusFor(t))} bonus</div>}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input type="number" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={`Importo personalizzato (min ${eur(w.topup.min_custom)})`}
            data-testid="topup-custom-input"
            className="flex-1 bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]" />
          <button onClick={() => topup(parseFloat(custom))} disabled={paying} data-testid="topup-custom-btn"
            className="flex items-center gap-2 bg-[#EA7B2B] text-black text-sm font-medium px-4 py-2.5 rounded-sm hover:bg-[#F59E4A] transition-colors disabled:opacity-50">
            {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ricarica
          </button>
        </div>
        <p className="text-[11px] text-zinc-500 mt-2">Pagamento sicuro tramite Stripe. Il saldo viene accreditato solo dopo la conferma del pagamento.</p>
      </Card>

      {/* Consumi per utente */}
      {w.consumi_per_utente?.length > 0 && (
        <Card title="Consumi per utente" icon={Users}>
          <table className="w-full text-sm">
            <thead><tr className="text-zinc-500 text-xs uppercase"><th className="text-left py-1.5">Utente</th><th className="text-right">Operazioni</th><th className="text-right">Spesa</th></tr></thead>
            <tbody>
              {w.consumi_per_utente.map((c) => (
                <tr key={c.user_id} className="border-t border-white/5">
                  <td className="py-2 text-white">{c.user_name || "—"}</td>
                  <td className="text-right text-zinc-400">{c.operazioni}</td>
                  <td className="text-right font-mono text-white">{eur(c.totale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Movimenti */}
      <Card title="Movimenti Wallet" icon={Receipt} testid="wallet-ledger">
        {ledger.length === 0 ? <p className="text-sm text-zinc-500">Nessun movimento.</p> : (
          <div className="space-y-1.5 max-h-80 overflow-auto">
            {ledger.map((m) => {
              const isCredit = ["ricarica", "bonus", "rimborso"].includes(m.tipo);
              return (
                <div key={m.id} className="flex items-center justify-between gap-2 py-2 border-b border-white/5">
                  <div className="min-w-0">
                    <div className="text-sm text-white capitalize">{m.tipo} {m.descrizione ? `· ${m.descrizione}` : ""}</div>
                    <div className="text-[11px] text-zinc-500">{new Date(m.created_at).toLocaleString("it-IT")}{m.user_name ? ` · ${m.user_name}` : ""}</div>
                  </div>
                  <div className={`font-mono text-sm shrink-0 ${isCredit ? "text-[#10B981]" : "text-[#F87171]"}`}>{isCredit ? "+" : "−"}{eur(m.importo)}</div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {user?.role === "admin" && <AdminPricing onSaved={load} />}
    </div>
  );
}

function AdminPricing({ onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [dash, setDash] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/pricing").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/due-diligence/catalog").then((r) => setCatalog(r.data.servizi)).catch(() => {});
    api.get("/admin/wallet/dashboard").then((r) => setDash(r.data)).catch(() => {});
  }, []);

  const setMarkup = (cat, v) => setCfg((c) => ({ ...c, pricing: { ...c.pricing, markup_categoria: { ...c.pricing.markup_categoria, [cat]: v === "" ? "" : parseFloat(v) } } }));
  const setMin = (key, v) => setCfg((c) => ({ ...c, pricing: { ...c.pricing, prezzo_min_servizio: { ...c.pricing.prezzo_min_servizio, [key]: v === "" ? undefined : parseFloat(v) } } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pricing", {
        markup_categoria: cfg.pricing.markup_categoria,
        prezzo_min_servizio: Object.fromEntries(Object.entries(cfg.pricing.prezzo_min_servizio || {}).filter(([, v]) => v != null)),
      });
      toast.success("Prezzi aggiornati");
      onSaved?.();
      api.get("/admin/wallet/dashboard").then((r) => setDash(r.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!cfg) return null;
  const k = dash?.kpi;
  return (
    <>
      <Card title="Admin · Dashboard economica" icon={BarChart3} testid="admin-econ-dashboard">
        {k ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Ricariche totali" value={eur(k.ricariche_totali)} />
            <Kpi label="Ricavi servizi" value={eur(k.ricavi_servizi)} />
            <Kpi label="Margine lordo" value={eur(k.margine_lordo)} sub={`${k.margine_pct}%`} accent />
            <Kpi label="MRR (mese)" value={eur(k.mrr)} />
            <Kpi label="ARPU" value={eur(k.arpu)} />
            <Kpi label="Bonus erogati" value={eur(k.bonus_erogati)} />
            <Kpi label="Servizi erogati" value={k.servizi_erogati} />
            <Kpi label="Organizzazioni" value={k.organizzazioni} />
          </div>
        ) : <p className="text-sm text-zinc-500">Nessun dato ancora.</p>}
        {dash?.top_servizi?.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] uppercase text-zinc-500 mb-1.5">Top servizi per ricavo</div>
            {dash.top_servizi.map((s) => (
              <div key={s.servizio} className="flex justify-between text-sm py-1 border-b border-white/5">
                <span className="text-white">{s.servizio} <span className="text-zinc-500">×{s.n}</span></span>
                <span className="font-mono text-white">{eur(s.ricavi)} <span className="text-[#10B981]">({eur(s.margine)})</span></span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Admin · Prezzi e markup" icon={Settings2} testid="admin-pricing" action={
        <button onClick={save} disabled={saving} data-testid="admin-pricing-save" className="flex items-center gap-2 bg-[#EA7B2B] text-black text-xs font-medium px-3 py-1.5 rounded-sm hover:bg-[#F59E4A] disabled:opacity-50">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Salva
        </button>
      }>
        <div className="text-[11px] uppercase text-zinc-500 mb-1.5">Markup per categoria (%)</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-5">
          {cfg.categorie.map((cat) => (
            <div key={cat} className="flex items-center gap-2">
              <label className="text-xs text-zinc-400 flex-1">{cat}</label>
              <input type="number" value={cfg.pricing.markup_categoria[cat] ?? ""} onChange={(e) => setMarkup(cat, e.target.value)}
                data-testid={`markup-${cat}`}
                className="w-20 bg-[#0A1826] border border-white/10 rounded-sm px-2 py-1.5 text-sm text-white text-right focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]" />
            </div>
          ))}
        </div>
        <div className="text-[11px] uppercase text-zinc-500 mb-1.5">Prezzo minimo per servizio (€)</div>
        <div className="space-y-1.5">
          {catalog.map((s) => (
            <div key={s.key} className="flex items-center gap-2 text-sm">
              <span className="flex-1 text-zinc-300 truncate">{s.label} <span className="text-zinc-600">· costo {eur(s.costo_provider)} → {eur(s.prezzo)}</span></span>
              <input type="number" placeholder="min" value={cfg.pricing.prezzo_min_servizio?.[s.key] ?? ""} onChange={(e) => setMin(s.key, e.target.value)}
                data-testid={`min-${s.key}`}
                className="w-24 bg-[#0A1826] border border-white/10 rounded-sm px-2 py-1.5 text-white text-right focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]" />
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

function Card({ title, icon: Icon, children, action, testid }) {
  return (
    <div className="bg-[#122A3F] border border-white/10 rounded-sm" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <h2 className="text-sm font-medium text-zinc-200 uppercase tracking-wider flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-[#EA7B2B]" />} {title}</h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function BalCard({ label, value, accent, icon: Icon, testid }) {
  return (
    <div className="bg-[#122A3F] border border-white/10 rounded-sm p-4" data-testid={testid}>
      <div className="text-[11px] uppercase text-zinc-500 flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5" />} {label}</div>
      <div className={`text-xl font-mono mt-1 ${accent ? "text-[#EA7B2B]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div className="bg-[#0A1826] border border-white/10 rounded-sm p-3">
      <div className="text-[10px] uppercase text-zinc-500">{label}</div>
      <div className={`text-lg font-mono ${accent ? "text-[#10B981]" : "text-white"}`}>{value}{sub && <span className="text-xs text-zinc-500 ml-1">{sub}</span>}</div>
    </div>
  );
}
